// Pure pieces of /admin/whatsapp-connect, kept free of JSX so Node's own test
// runner can load them (web/test/whatsappConnect.test.ts).
//
// Everything here about Meta's side was read on 4 October 2026 from:
//   Implementation (v4):
//   https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation/
//   WhatsApp Business app onboarding ("coexistence"):
//   https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users/
//   Versions:
//   https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/versions/

/** The SDK script, exactly as Meta's implementation page loads it. */
export const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js'

// FB.init's version, which also decides the dialog URL (facebook.com/<v>/
// dialog/oauth). Meta's Embedded Signup code builder for this app (WhatsApp use
// case, Embedded Signup Builder, 12 October 2026) initialises with v26.0. With
// v25.0 the live flow showed the standard number screen and never the
// WhatsApp Business app option, so the browser follows the builder. The server
// stays on v25.0 (supabase/functions/_shared/whatsapp.ts), supported to 2028;
// moving it is its own change.
export const FB_SDK_GRAPH_VERSION = 'v26.0'

export type FbLoginOptions = {
  config_id: string
  response_type: 'code'
  override_default_response_type: true
  extras: Record<string, unknown>
}

/**
 * The second argument to FB.login, exactly as Meta's coexistence page
 * ("Step 2: Customize Embedded Signup", read 12 October 2026) gives it:
 * extras { setup: {}, featureType: 'whatsapp_business_app_onboarding',
 * sessionInfoVersion: '3' }.
 *
 * WHY sessionInfoVersion IS BACK. The first build left it out, reading the
 * Versions page's "the extras object is purposely empty for v4" as making it
 * a v2 relic. The first live tests on 12 October 2026 proved that wrong: with
 * a v4 configuration and every coexistence webhook field subscribed, Meta
 * showed the standard "Add your WhatsApp phone number" screen instead of the
 * option to connect the existing WhatsApp Business app. That screen
 * registers the number on Cloud API alone and takes it off the phone, so it
 * must never be finished. Do not remove this field again without a live test
 * that shows the coexistence screen without it.
 *
 * WHY version: 'v4'. With sessionInfoVersion alone the standard screen still
 * came back. Meta's own generator (WhatsApp use case, Become Tech Provider,
 * Meta-hosted embedded signup link, 12 October 2026) builds this
 * configuration's link with extras { version: 'v4', sessionInfoVersion: '3',
 * featureType: 'whatsapp_business_app_onboarding' }, which no developer page
 * shows. This now matches that generator field for field.
 */
export function loginOptions(configId: string): FbLoginOptions {
  return {
    config_id: configId,
    response_type: 'code',
    override_default_response_type: true,
    extras: {
      setup: {},
      version: 'v4',
      featureType: 'whatsapp_business_app_onboarding',
      sessionInfoVersion: '3',
    },
  }
}

export type SignupMessage =
  | {
      kind: 'finish'
      event: string
      waba_id: string | null
      phone_number_id: string | null
      business_id: string | null
    }
  | { kind: 'cancel'; current_step: string | null }
  | { kind: 'error'; error_message: string; error_code: string | null; session_id: string | null }

const FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
])

/**
 * Meta's sample accepts any origin that ends with 'facebook.com', which a
 * domain like evilfacebook.com also does. This accepts facebook.com and its
 * subdomains over https only.
 */
export function isFacebookOrigin(origin: string): boolean {
  try {
    const u = new URL(origin)
    return u.protocol === 'https:' && (u.hostname === 'facebook.com' || u.hostname.endsWith('.facebook.com'))
  } catch {
    return false
  }
}

const idOrNull = (v: unknown): string | null => (typeof v === 'string' && /^\d{5,20}$/.test(v) ? v : null)
const textOrNull = (v: unknown): string | null => (v == null || v === '' ? null : String(v))

/** The session logging message, or null for anything that is not one. */
export function parseSignupMessage(origin: string, raw: unknown): SignupMessage | null {
  if (!isFacebookOrigin(origin)) return null
  let data: Record<string, unknown>
  try {
    data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown>
  } catch {
    return null
  }
  if (!data || typeof data !== 'object' || data.type !== 'WA_EMBEDDED_SIGNUP') return null
  const event = String(data.event ?? '')
  const d = (data.data ?? {}) as Record<string, unknown>
  if (FINISH_EVENTS.has(event)) {
    return {
      kind: 'finish',
      event,
      waba_id: idOrNull(d.waba_id),
      phone_number_id: idOrNull(d.phone_number_id),
      business_id: idOrNull(d.business_id),
    }
  }
  // A user reported error also arrives as CANCEL, carrying error_message.
  if (event === 'ERROR' || d.error_message) {
    return {
      kind: 'error',
      error_message: String(d.error_message ?? 'Meta reported an error inside the signup window.'),
      error_code: textOrNull(d.error_code),
      session_id: textOrNull(d.session_id),
    }
  }
  if (event === 'CANCEL') return { kind: 'cancel', current_step: textOrNull(d.current_step) }
  return null
}

/** What the person must confirm before Connect is offered. */
export const CHECKLIST: Array<{ id: string; text: string }> = [
  {
    id: 'version',
    text: 'The phone runs WhatsApp Business app version 2.24.17 or later.',
  },
  {
    id: 'history_once',
    text: 'I understand the choice to share chat history with Fleetly is made once, during this flow, and cannot be made again without disconnecting and starting over.',
  },
  {
    id: 'broadcasts',
    text: 'I understand that after connecting, broadcast lists stop: no new ones can be made and existing ones become read only.',
  },
  {
    id: 'one_to_one',
    text: 'I understand that in one to one chats, disappearing messages, view once messages and live location stop.',
  },
  {
    id: 'groups',
    text: 'I understand that group chats stay in the app only. They are not synced and cannot be answered from the cockpit.',
  },
  {
    id: 'fourteen_days',
    text: 'I will open the WhatsApp Business app on the phone at least once every 14 days, or Meta disconnects the number from Fleetly.',
  },
]

export type ConnectStatus = {
  allowed: boolean
  enabled?: boolean
  meta_app_id?: string | null
  es_config_id?: string | null
  ready?: boolean
  connected?: {
    attempt_id: number
    waba_id: string | null
    phone_number_id: string | null
    display_phone_number: string | null
    business_id: string | null
    connected_at: string | null
    steps: Record<string, { ok?: boolean; request_id?: string }>
  } | null
  last_attempt?: {
    attempt_id: number
    outcome: 'started' | 'connected' | 'failed' | 'cancelled'
    flow_event: string | null
    error_text: string | null
    started_at: string
    finished_at: string | null
    token_stored: boolean
    sync_deadline: string | null
    by: string
  } | null
  resumable?: boolean
}

export type PageState = 'not_permitted' | 'off' | 'no_config' | 'connected' | 'failed' | 'ready'

/** Which of the page's sentences applies. The order is the point. */
export function pageState(s: ConnectStatus): PageState {
  if (!s.allowed) return 'not_permitted'
  if (!s.enabled) return 'off'
  if (!s.ready || !s.meta_app_id || !s.es_config_id) return 'no_config'
  // A failure after the connection was made outranks the older success,
  // because it is the one that still needs a person.
  const last = s.last_attempt
  if (last && last.outcome === 'failed' && (!s.connected || last.attempt_id > s.connected.attempt_id)) return 'failed'
  if (s.connected) return 'connected'
  if (last && last.outcome === 'failed') return 'failed'
  return 'ready'
}
